import { getTrackList } from "@api/tracklists";
import type { Tracklist } from "@api/tracklists/types";
import { GalleryContext } from "@components/Gallery/contexts/GalleryContext";
import { Card } from "@components/Layout";
import Multiply from "@components/Multiply";
import PlayableTrack from "@components/PlayableTrack";
import PlayableTrackTemplate from "@components/PlayableTrack/index.template";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import useDocumentTitle from "@hooks/useDocumentTitle";
import { activeFeedContext } from "@layouts/App/context/ActiveFeed";
import usePlay from "@layouts/App/context/Player/hooks/usePlay";
import { useLocation, useNavigate, useParams } from "@solidjs/router";
import {
	ErrorBoundary,
	Index,
	Show,
	type VoidComponent,
	createMemo,
	createRenderEffect,
	createResource,
	createSignal,
	useContext,
} from "solid-js";
import UuidEncoder from "uuid-encoder";
import Comments from "./components/Comments";
import Header from "./components/Header";
import HeaderTemplate from "./components/Header/index.template";
import MobileTrack from "./components/MobileTrack";
import TracklistDrawer from "./components/TracklistDrawer";
import useControlTracklist from "./hooks/useControlTracklist";
import { DesktopTracksWrapper, TracklistViewWrapper } from "./styled";

const TracklistColumn: VoidComponent<{ tracklist: Tracklist; onReverse: () => void }> = (props) => {
	const { tracklist, onBookmark, onLike, onTrackBookmark } = useControlTracklist(props.tracklist);
	const { playTracks, playTracklist } = usePlay();
	const location = useLocation();
	const params = useParams<{ index: string }>();
	const { setIndex, setPhotos } = useContext(GalleryContext);
	useDocumentTitle(props.tracklist.name);

	const onImageClick = (index: number) => {
		setPhotos([props.tracklist.photo, ...props.tracklist.tracks.map((tr) => tr.photo)]);
		setIndex(index);
	};
	const publicProgramme = createMemo(() => location.pathname.includes("/public/"));
	const isTrackPublic = (index: number) => publicProgramme() && Number(params.index) !== index;
	return (
		<>
			<Header
				tracklist={{ ...tracklist, tracks: tracklist.tracks.filter((_track, i) => !isTrackPublic(i)) }}
				onBookmark={onBookmark}
				onLike={onLike}
				public={publicProgramme()}
				onReverse={() => {}}
			/>
			<DesktopTracksWrapper>
				<Index each={tracklist.tracks}>
					{(track, index) => (
						<PlayableTrack
							track={track()}
							index={index}
							tracklist={tracklist}
							onBookmark={() => onTrackBookmark(index)}
							onPlay={
								publicProgramme()
									? isTrackPublic(index)
										? () => {}
										: () => playTracks([track()], 0, { type: "TRACK" })
									: () => playTracklist(tracklist, index, true)
							}
							even={index % 2 === 0}
							blur={isTrackPublic(index)}
							public={publicProgramme()}
							onImageClick={() => onImageClick(index + 1)}
						/>
					)}
				</Index>
			</DesktopTracksWrapper>
			<TracklistDrawer>
				<Index each={tracklist.tracks}>
					{(track, index) => (
						<MobileTrack
							track={track()}
							index={index}
							tracklist={tracklist}
							onBookmark={() => onTrackBookmark(index)}
							onPlay={() => playTracklist(tracklist, index, true)}
							public={publicProgramme()}
							onImageClick={() => onImageClick(index + 1)}
						/>
					)}
				</Index>
			</TracklistDrawer>
		</>
	);
};

const Inner: VoidComponent = () => {
	const params = useParams<{ uuid: string }>();
	const { tracklists } = useContext(activeFeedContext);

	const [tracklist, { mutate }] = createResource(
		() => params.uuid,
		(uuid) => getTrackList(uuid, true),
		{
			initialValue: tracklists.find((tr) => tr?.uuid === params.uuid),
		},
	);

	const onReverse = () => {
		const tr = tracklist.latest;
		if (!tr) return;

		mutate({ ...tr, tracks: tr.tracks.toReversed() });
	};

	return (
		<>
			<Show
				when={tracklist.latest}
				keyed
				fallback={
					<>
						<HeaderTemplate />
						<Multiply count={4}>
							<PlayableTrackTemplate />
						</Multiply>
					</>
				}
			>
				{(tr) => <TracklistColumn tracklist={tr} onReverse={onReverse} />}
			</Show>
			<Comments uuid={params.uuid} />
		</>
	);
};

const TracklistDetailView = () => {
	const { t } = useI18n();
	const [show, setShow] = createSignal(false);
	const params = useParams<{ uuid: string; index?: string }>();
	const navigate = useNavigate();

	createRenderEffect(() => {
		const uuid = params.uuid;
		if (uuid.length !== 36) {
			const encoder = new UuidEncoder("base64url");
			return navigate(`/tracklist/${encoder.decode(uuid)}/${params.index ?? ""}`);
		}
		setShow(true);
	});

	return (
		<TracklistViewWrapper alignItems="center">
			<Show when={show()}>
				<ErrorBoundary
					fallback={
						<Card maxWidth="756px" padding="20px" justifyContent="center" alignItems="center">
							<h3>{t("app.programme.notFound")}</h3>
						</Card>
					}
				>
					<Inner />
				</ErrorBoundary>
			</Show>
		</TracklistViewWrapper>
	);
};
export default TracklistDetailView;
